import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { finalize } from 'rxjs/operators';

import { AuthService } from '../../../core/services/auth.service';
import { ImpersonationService } from '../../../core/services/impersonation.service';
import { ImpersonationBroadcastService } from '../../../core/services/impersonation-broadcast.service';
import { leaveImpersonationHost } from '../../../core/utils/vanity-url.util';

@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './impersonation-banner.component.html',
  styleUrls: ['./impersonation-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImpersonationBannerComponent {
  private readonly authService = inject(AuthService);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly impersonationBroadcast = inject(ImpersonationBroadcastService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly profile$ = this.authService.userProfile$;

  isEnding = false;
  errorMessage: string | null = null;

  returnToAdminAccount(): void {
    if (this.isEnding) {
      return;
    }

    this.isEnding = true;
    this.errorMessage = null;

    this.impersonationService
      .end()
      .pipe(
        finalize(() => {
          this.isEnding = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.impersonationBroadcast.notifyChanged();
          leaveImpersonationHost();
        },
        error: (error: unknown) => {
          // A dead or already-ended session cannot be ended again: the admin's own session is what is
          // left, so reload instead of leaving the banner stuck on "RETURNING...".
          const status = (error as { status?: number })?.status;
          if (status === 401 || status === 409) {
            this.impersonationBroadcast.notifyChanged();
            window.location.replace('/');
            return;
          }

          this.errorMessage = this.resolveError(error);
          this.cdr.markForCheck();
        },
      });
  }

  private resolveError(error: unknown): string {
    const response = error as {
      error?: string | {
        message?: string;
        error?: string;
        errorMessage?: string;
        title?: string;
      };
      message?: string;
    };

    if (typeof response?.error === 'string' && response.error.trim()) {
      return response.error;
    }

    if (response?.error && typeof response.error === 'object') {
      return (
        response.error.message ??
        response.error.errorMessage ??
        response.error.error ??
        response.error.title ??
        response.message ??
        'Unable to return to the admin account.'
      );
    }

    return response?.message ?? 'Unable to return to the admin account.';
  }
}